import React from 'react';
import LANGUAGES_CONTROLLER from '../../../controllers/languages';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import { NON_ACTIONABLE_STATUSES, resolveStatusKey, getProcessingStatusLabel } from '../../../utils/uploadedDocumentsHelper';
import { STableContainer, STooltip, SIcon } from '@avalara/skylab-sdk/react';
import ProcessingStatusTag from '../shared/ProcessingStatusTag';
import InvoiceStatusTag from '../shared/InvoiceStatusTag';
import ActionColumnCell from '../shared/ActionColumnCell';

const renderFileName = (document, statusKey, loadDocumentDetails) => {
    const isActionable = !NON_ACTIONABLE_STATUSES.has(statusKey);
    if (isActionable) {
        return <a onClick={loadDocumentDetails} className="tertiary">{document.fileName}</a>;
    }
    return document.fileName;
};

const renderCountHeader = (labelString, parentTotal, columnKey, parentId, languages) => {
    const iconId = `parent-total-info-${columnKey}-${parentId}`;

    return (
        <th className="count-column">
            <span className="flex align-items-center" style={{ gap: '0.25rem' }}>
                <LanguageStringResolver string={labelString} />
                <SIcon id={iconId} name="info-circle" style={{ cursor: 'pointer' }} />
            </span>
            <STooltip triggerId={iconId} tooltipId={`${iconId}-tooltip`} placement="top">
                <span slot="content">{LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_parentTotalTooltip', [parentTotal])}</span>
            </STooltip>
        </th>
    );
};

const ChildrenDetails = ({ childRows, fieldValues, languages, parentDocument, getOnSelect, getLoadDocumentDetails }) => {
    if (!childRows || childRows.length === 0) {
        return null;
    }

    const parentProcessedCount = parentDocument.processedCount || 0;
    const parentErrorCount = parentDocument.errorCount || 0;
    const parentOnholdCount = parentDocument.onholdCount || 0;
    const parentInboxCount = parentDocument.inboxCount || 0;
    const parentTotalNumber = parentProcessedCount + parentErrorCount + parentOnholdCount + parentInboxCount;

    return (
        <div className="children-details-table-wrapper">
            <STableContainer>
                <table className="s-table child-documents-table">
                    <thead>
                        <tr>
                            <th className="filename-column"><LanguageStringResolver string="uploadedDocuments_fileName" /></th>
                            <th className="status-column"><LanguageStringResolver string="uploadedDocuments_fileStatus" /></th>
                            <th className="status-column"><LanguageStringResolver string="uploadedDocuments_invoiceStatus" /></th>
                            {renderCountHeader('uploadedDocuments_processed', parentProcessedCount, 'processed', parentDocument.id, languages)}
                            {renderCountHeader('uploadedDocuments_error', parentErrorCount, 'error', parentDocument.id, languages)}
                            {renderCountHeader('uploadedDocuments_onHold', parentOnholdCount, 'onhold', parentDocument.id, languages)}
                            {renderCountHeader('uploadedDocuments_pending', parentInboxCount, 'pending', parentDocument.id, languages)}
                            {renderCountHeader('uploadedDocuments_totalNumber', parentTotalNumber, 'total', parentDocument.id, languages)}
                            <th className="checkbox-column"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {childRows.map((document) => {
                            const statusKey = resolveStatusKey(document.processingStatus);
                            const statusLabel = getProcessingStatusLabel(languages, statusKey);
                            const totalNumber = (document.processedCount || 0) +
                                (document.errorCount || 0) +
                                (document.onholdCount || 0) +
                                (document.inboxCount || 0);
                            const selected = fieldValues && fieldValues[document.id + ''];
                            const onSelect = getOnSelect(document.id);

                            return (
                                <tr key={document.id} className="child-document-row" data-testid="uploaded-doc-child-row">
                                    <td className="filename-column">
                                        <span className="filename-text">
                                            {renderFileName(document, statusKey, getLoadDocumentDetails(document.id))}
                                        </span>
                                    </td>
                                    <td className="status-column">
                                        <ProcessingStatusTag document={document} languages={languages} />
                                    </td>
                                    <td className="status-column">
                                        <InvoiceStatusTag document={document} languages={languages} />
                                    </td>
                                    <td className="count-column">{document.processedCount}</td>
                                    <td className="count-column">{document.errorCount}</td>
                                    <td className="count-column">{document.onholdCount}</td>
                                    <td className="count-column">{document.inboxCount}</td>
                                    <td className="count-column">{totalNumber}</td>
                                    <ActionColumnCell
                                        document={document}
                                        statusKey={statusKey}
                                        statusLabel={statusLabel}
                                        selected={selected}
                                        onChange={() => onSelect(!selected)}
                                        languages={languages}
                                    />
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </STableContainer>
        </div>
    );
};


ChildrenDetails.defaultProps = {
    childRows: [],
    languages: {}
};

export default ChildrenDetails;
